import type { ResidentItem } from '@lmc/ts-common'
import { ImportantContactType, roleOptions } from '@lmc/ts-common'
import {
  Add,
  Button,
  Chevron,
  EmptyState,
  Heading,
  NotStartedIcon,
  PageHeader,
  Tag
} from '@lmc/ui-kit'
import type { ResidentFetchItemResponseImportantContact } from '@lmc/validations'
import Actions from 'actions'
import { Dropdown, type MenuProps } from 'antd'
import type { DescriptionsItemType } from 'antd/es/descriptions'
import { useCanEditServiceUserData } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import useFeatures from 'hooks/useFeatures'
import useResident from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { resendInvite } from 'operations/Users'
import { useEffect, useRef, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import MenuGrid from 'screens/Residents/components/MenuGrid/MenuGrid'
import styled from 'styled-components'
import { ProfileViewSection } from '../ResidentProfileScreen/components/ProfileViewSection'
import { ProfileContent } from '../ResidentProfileScreen/styledComponents'
import { getAddressString } from '../ResidentProfileScreen/utils/getAddressString'
import { scrollToTop } from '../ResidentProfileScreen/utils/utils'
import FamilyView from './forms/components/FamilyView'
import type { ImportantContactDependantDetailsForm } from './forms/dependantDetailsFormTypes'
import type { ContactStatus } from './forms/PersonalContactForm'
import { relationshipOptions } from './forms/utils/relationshipOptions'
import { formatDate } from './utils/importantContactDisplayUtils'

const isDesktop = isBrowser || isTablet

export const ResidentImportantPeopleScreen = ({
  testId
}: {
  testId?: string
} = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isSendingInvite, setIsSendingInvite] = useState(false)
  const features = useFeatures()
  const canEditServiceUserData = useCanEditServiceUserData()

  const openFamilyPortalModal = (
    contact: ResidentFetchItemResponseImportantContact,
    index: number,
    profile: ResidentItem
  ) => {
    const modalID = 'inviteFamily'

    dispatch(
      Actions.openModal({
        modalId: modalID,
        data: {
          residentId: profile.id,
          residentName: {
            first: profile.name.first,
            last: profile.name.last
          },
          importantContact: contact,
          index
        }
      })
    )
  }

  const topRef = useRef<HTMLDivElement | null>(null)
  const {
    params: { id }
  } = useRouter()

  const { resident, isLoading } = useResident({ id })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId
            ? `${testId}-important-people-loading`
            : 'important-people-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId
            ? `${testId}-important-people-no-resident`
            : 'important-people-no-resident'
        }
      />
    )
  }

  const importantContacts = resident.importantContacts as
    | ResidentFetchItemResponseImportantContact[]
    | undefined

  const residentName = `${resident.name.first} ${resident.name.last}`

  const appendIndexToImportantContact = (
    importantContact: ResidentFetchItemResponseImportantContact,
    index: number
  ): ResidentFetchItemResponseImportantContact & { index: number } => ({
    ...importantContact,
    index
  })

  const isProfessionalContactType = (
    type: ResidentFetchItemResponseImportantContact['type'] | undefined
  ) => type === ImportantContactType.professional

  const personalContacts =
    importantContacts
      ?.map(appendIndexToImportantContact)
      .filter((contact) => !isProfessionalContactType(contact.type)) || []
  const professionalContacts =
    importantContacts
      ?.map(appendIndexToImportantContact)
      .filter((contact) => isProfessionalContactType(contact.type)) || []

  const hasGP = Boolean(
    resident.gpAddress ||
      resident.gpCode ||
      resident.gpEmail ||
      resident.gpName ||
      resident.gpSurgeryName ||
      resident.gpTelephone
  )
  const hasProfessionalContacts = professionalContacts.length > 0
  const hasPersonalContacts = personalContacts.length > 0

  const hasItems = hasGP || hasProfessionalContacts || hasPersonalContacts

  return (
    <div
      ref={topRef}
      data-testid={
        testId ? `${testId}-important-people-screen` : 'important-people-screen'
      }
    >
      <PageHeader
        sticky
        title={`${residentName}'s ${t(supConstants.IMPORTANT_PEOPLE)}`}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () => navigate(`/residents/${id}/profile`, { replace: true })
            : undefined
        }
        data-testid={
          testId
            ? `${testId}-important-people-header`
            : 'important-people-header'
        }
      >
        <Dropdown
          menu={{ items: getDropdownItems(t, hasGP) }}
          trigger={['click']}
          disabled={!canEditServiceUserData}
          data-testid={
            testId
              ? `${testId}-important-people-dropdown`
              : 'important-people-dropdown'
          }
        >
          <Button
            iconAlign="right"
            variant="primary"
            disabledByPermission={!canEditServiceUserData}
            data-testid={
              testId
                ? `${testId}-important-people-add-contact-btn`
                : 'important-people-add-contact-btn'
            }
          >
            {t(supConstants.ADD_CONTACT)}
            <Chevron direction="down" />
          </Button>
        </Dropdown>
      </PageHeader>
      <StyledWrapper
        data-testid={
          testId
            ? `${testId}-important-people-content`
            : 'important-people-content'
        }
      >
        {hasItems ? (
          <>
            {hasGP && (
              <StyledSectionsWrapper
                data-testid={
                  testId
                    ? `${testId}-important-people-gp-section`
                    : 'important-people-gp-section'
                }
              >
                <HeadingWithMargins
                  level={1}
                  data-testid={
                    testId
                      ? `${testId}-important-people-gp-heading`
                      : 'important-people-gp-heading'
                  }
                >
                  {t(supConstants.GP)}
                </HeadingWithMargins>
                <MenuGridStyled
                  data-testid={
                    testId
                      ? `${testId}-important-people-gp-grid`
                      : 'important-people-gp-grid'
                  }
                >
                  <ProfileViewSection
                    isEditMode
                    title={resident.gpName || ''}
                    items={getGPContactViewSectionItems(resident, t)}
                    link="edit-gp"
                    testId={testId}
                  />
                </MenuGridStyled>
              </StyledSectionsWrapper>
            )}
            {hasProfessionalContacts && (
              <StyledSectionsWrapper
                data-testid={
                  testId
                    ? `${testId}-important-people-professional-section`
                    : 'important-people-professional-section'
                }
              >
                <HeadingWithMargins
                  level={1}
                  data-testid={
                    testId
                      ? `${testId}-important-people-professional-heading`
                      : 'important-people-professional-heading'
                  }
                >
                  {t(supConstants[ImportantContactType.professional])}
                </HeadingWithMargins>
                <MenuGridStyled
                  data-testid={
                    testId
                      ? `${testId}-important-people-professional-grid`
                      : 'important-people-professional-grid'
                  }
                >
                  {professionalContacts?.map((contact) => (
                    <ProfileViewSection
                      isEditMode
                      key={contact.index}
                      link={`${contact.index}/edit-professional`}
                      title={`${contact.name?.first} ${contact.name?.last}`}
                      items={getProfessionalProfileViewSectionItems(contact, t)}
                      subtitle={
                        <div
                          data-testid={
                            testId
                              ? `${testId}-important-people-professional-tags-${contact.index}`
                              : `important-people-professional-tags-${contact.index}`
                          }
                        >
                          {contact.lpaTypes?.map((lpaType) => (
                            <Tag
                              key={lpaType}
                              color="purple"
                              data-testid={
                                testId
                                  ? `${testId}-important-people-lpa-tag-${lpaType}`
                                  : `important-people-lpa-tag-${lpaType}`
                              }
                            >
                              {t(supConstants[`LPA_${lpaType}`])}
                            </Tag>
                          ))}
                          {contact.proxyForSubjectOfCare && (
                            <Tag color="green">{t(supConstants.PROXY)}</Tag>
                          )}
                        </div>
                      }
                      testId={testId}
                    >
                      {features?.family?.enabled && contact.familyHasAccess && (
                        <FamilyViewStyled
                          contact={contact}
                          residentName={resident.name.first}
                          items={familyPortalViewSectionItems(contact, t)}
                          title={t(supConstants.FAMILY_PORTAL)}
                          hasAccess={Boolean(contact.familyHasAccess)}
                          hasBeenInvited={Boolean(contact.familyDateInvited)}
                          hasAcceptedInvitation={Boolean(
                            contact.familyDateActivated
                          )}
                          disabledByPermission={!canEditServiceUserData}
                          onCreateInvite={() =>
                            openFamilyPortalModal(
                              contact,
                              contact.index,
                              resident
                            )
                          }
                          isSendingInvite={isSendingInvite}
                          onResendInvite={() => {
                            setIsSendingInvite(true)
                            resendInvite(contact.familyAccountId, () =>
                              setIsSendingInvite(false)
                            )()
                          }}
                        />
                      )}
                    </ProfileViewSection>
                  ))}
                </MenuGridStyled>
              </StyledSectionsWrapper>
            )}
            {hasPersonalContacts && (
              <StyledSectionsWrapper>
                <HeadingWithMargins level={1}>
                  {t(supConstants[ImportantContactType.personal])}
                </HeadingWithMargins>
                <MenuGridStyled>
                  {personalContacts?.map((contact) => (
                    <ProfileViewSection
                      isEditMode
                      key={contact.index}
                      link={`${contact.index}/edit-personal`}
                      title={`${contact.name?.first} ${contact.name?.last}`}
                      items={getPersonalProfileViewSectionItems(contact, t)}
                      subtitle={
                        <div>
                          {contact.lpaTypes?.map((lpaType) => (
                            <Tag color="purple" key={lpaType}>
                              {t(supConstants[`LPA_${lpaType}`])}
                            </Tag>
                          ))}
                          {contact.nextOfKin && (
                            <Tag color="magenta">
                              {t(supConstants.NEXT_OF_KIN)}
                            </Tag>
                          )}
                          {contact.isDependant === true && (
                            <Tag color="gold">{t(supConstants.DEPENDANT)}</Tag>
                          )}
                          {contact.unpaidCarer && (
                            <Tag color="geekblue">
                              {t(supConstants.UNPAID_CARER)}
                            </Tag>
                          )}
                          {contact.proxyForSubjectOfCare && (
                            <Tag color="green">{t(supConstants.PROXY)}</Tag>
                          )}
                        </div>
                      }
                    >
                      {features?.family?.enabled && contact.familyHasAccess && (
                        <FamilyViewStyled
                          contact={contact}
                          residentName={resident.name.first}
                          items={familyPortalViewSectionItems(contact, t)}
                          title={t(supConstants.FAMILY_PORTAL)}
                          hasAccess={Boolean(contact.familyHasAccess)}
                          hasBeenInvited={Boolean(contact.familyDateInvited)}
                          hasAcceptedInvitation={Boolean(
                            contact.familyDateActivated
                          )}
                          disabledByPermission={!canEditServiceUserData}
                          onCreateInvite={() =>
                            openFamilyPortalModal(
                              contact,
                              contact.index,
                              resident
                            )
                          }
                          isSendingInvite={isSendingInvite}
                          onResendInvite={() => {
                            setIsSendingInvite(true)
                            resendInvite(contact.familyAccountId, () =>
                              setIsSendingInvite(false)
                            )()
                          }}
                        />
                      )}
                    </ProfileViewSection>
                  ))}
                </MenuGridStyled>
              </StyledSectionsWrapper>
            )}
          </>
        ) : (
          <EmptyState
            icon={<NotStartedIcon />}
            title={t(supConstants.READY_SET_GO_THE_JOURNEY_STARTS_HERE)}
            description={t(
              supConstants.PRESS_THAT_BLUE_BUTTON_AT_THE_TOP_RIGHT_OF_THE_PAGE
            )}
            data-testid={
              testId
                ? `${testId}-important-people-empty-state`
                : 'important-people-empty-state'
            }
          />
        )}
      </StyledWrapper>
    </div>
  )
}

const getDropdownItems = (
  t: (key: string) => string,
  hasGP: boolean
): MenuProps['items'] => {
  const items: { key: string | number; link: string; title: string }[] =
    Object.values(ImportantContactType).map((value) => ({
      key: value,
      title: t(supConstants[value]),
      link: `add-${value}`
    }))

  if (!hasGP) {
    items.push({
      key: 'gp',
      title: t(supConstants.GP),
      link: 'edit-gp'
    })
  }

  const renderedItems = items.map((item) => ({
    label: (
      <LinkStyled key={item.key} to={item.link}>
        <Add />
        {item.title}
      </LinkStyled>
    ),
    key: item.key
  }))

  return renderedItems
}

const LinkStyled = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const createMailtoLink = (email: string | undefined) =>
  email && <Link to={`mailto:${email}`}>{email}</Link>

const getProfessionalProfileViewSectionItems = (
  contact: ResidentFetchItemResponseImportantContact,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: 'role',
      label: t(supConstants.ROLE),
      children:
        roleOptions?.find((option) => option.value === contact.relationship)
          ?.label || contact.relationship
    },
    {
      key: 'organisation',
      label: t(supConstants.ORGANISATION),
      children: contact.organisation
    },
    {
      key: 'speciality',
      label: t(supConstants.SPECIALITY),
      children: contact.speciality
    },
    {
      key: 'email',
      label: t(supConstants.EMAIL),
      children: createMailtoLink(contact.email)
    },
    {
      key: 'address',
      label: t(supConstants.ADDRESS),
      children: getAddressString(
        contact.address as Parameters<typeof getAddressString>[0]
      )
    },
    {
      key: 'phone',
      label: t(supConstants.TELEPHONE1),
      children: contact.telephone
    },
    {
      key: 'phone2',
      label: t(supConstants.TELEPHONE2),
      children: contact.telephoneSecondary
    },
    {
      key: 'contactStatus',
      label: t(supConstants.CONTACT_STATUS),
      children: t(supConstants[contact.contactStatus as ContactStatus])
    },
    {
      key: 'observations',
      label: t(supConstants.OBSERVATION_NOTES),
      children: contact.observations
    },
    {
      key: 'contactableTimes',
      label: t(supConstants.CONTACTABLE_TIMES),
      children: contact.contactableTimes
    },
    {
      key: 'familyComment',
      label: t(supConstants.JUSTIFICATION_OF_DECISION),
      children: contact.familyComment
    }
  ].filter((item) => item.children)
}

const getPersonalProfileViewSectionItems = (
  contact: ResidentFetchItemResponseImportantContact,
  t: (key: string) => string
): DescriptionsItemType[] => {
  const dep = contact.dependantDetails as
    | ImportantContactDependantDetailsForm
    | undefined

  const dependantRows: DescriptionsItemType[] = (() => {
    if (contact.isDependant !== true || !dep) {
      return []
    }

    const rows: DescriptionsItemType[] = []

    if (dep.dateOfBirth?.trim()) {
      rows.push({
        key: 'dependantDateOfBirth',
        label: t(supConstants.DEPENDANT_DATE_OF_BIRTH),
        children: formatDate(dep.dateOfBirth)
      })
    }
    if (dep.dateOfObservation?.trim()) {
      rows.push({
        key: 'dependantDateOfObservation',
        label: t(supConstants.DATE_OF_OBSERVATION),
        children: formatDate(dep.dateOfObservation)
      })
    }

    const linkedName = [
      dep.linkedContactName?.first,
      dep.linkedContactName?.last
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ')
    if (linkedName) {
      rows.push({
        key: 'dependantLinkedName',
        label: t(supConstants.DEPENDANT_VIEW_LINKED_NAME),
        children: linkedName
      })
    }
    if (dep.linkedContactRelationship?.trim()) {
      rows.push({
        key: 'dependantLinkedRelationship',
        label: t(supConstants.DEPENDANT_VIEW_LINKED_RELATIONSHIP),
        children: dep.linkedContactRelationship.trim()
      })
    }
    if (dep.linkedContactTelephone?.trim()) {
      rows.push({
        key: 'dependantLinkedTel1',
        label: t(supConstants.DEPENDANT_VIEW_LINKED_TEL1),
        children: dep.linkedContactTelephone.trim()
      })
    }
    if (dep.linkedContactTelephoneSecondary?.trim()) {
      rows.push({
        key: 'dependantLinkedTel2',
        label: t(supConstants.DEPENDANT_VIEW_LINKED_TEL2),
        children: dep.linkedContactTelephoneSecondary.trim()
      })
    }
    if (dep.linkedContactEmail?.trim()) {
      rows.push({
        key: 'dependantLinkedEmail',
        label: t(supConstants.DEPENDANT_VIEW_LINKED_EMAIL),
        children: createMailtoLink(dep.linkedContactEmail.trim())
      })
    }

    return rows
  })()

  return [
    {
      key: 'relationship',
      label: t(supConstants.RELATIONSHIP),
      children:
        relationshipOptions.find(
          (option) => option.value === contact.relationship
        )?.label || contact.relationship
    },
    ...dependantRows,
    {
      key: 'email',
      label: t(supConstants.EMAIL),
      children: createMailtoLink(contact.email)
    },
    {
      key: 'address',
      label: t(supConstants.ADDRESS),
      children: getAddressString(
        contact.address as Parameters<typeof getAddressString>[0]
      )
    },
    {
      key: 'phone',
      label: t(supConstants.TELEPHONE1),
      children: contact.telephone
    },
    {
      key: 'phone2',
      label: t(supConstants.TELEPHONE2),
      children: contact.telephoneSecondary
    },
    {
      key: 'contactStatus',
      label: t(supConstants.CONTACT_STATUS),
      children: t(supConstants[contact.contactStatus as ContactStatus])
    },
    {
      key: 'observations',
      label: t(supConstants.OBSERVATION_NOTES),
      children: contact.observations
    },
    {
      key: 'contactableTimes',
      label: t(supConstants.CONTACTABLE_TIMES),
      children: contact.contactableTimes
    },
    {
      key: 'familyComment',
      label: t(supConstants.JUSTIFICATION_OF_DECISION),
      children: contact.familyComment
    }
  ].filter((item) => item.children)
}

const getGPContactViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: '1',
      label: t(supConstants.SURGERY_PRACTICE_NAME),
      children: resident.gpSurgeryName
    },
    {
      key: '2',
      label: t(supConstants.PRACTICE_CODE),
      children: resident.gpCode
    },
    {
      key: '3',
      label: t(supConstants.EMAIL),
      children: resident.gpEmail
    },
    {
      key: '4',
      label: t(supConstants.ADDRESS),
      children: getAddressString(resident.gpAddress)
    },
    {
      key: '5',
      label: t(supConstants.MOBILE_NUMBER),
      children: resident.gpTelephone
    }
  ].filter((item) => item.children)
}

const familyPortalViewSectionItems = (
  contact: ResidentFetchItemResponseImportantContact,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: '1',
      label: t(supConstants.ACTIVE_SINCE),
      children: contact.familyDateActivated
    },
    {
      key: '2',
      label: t(supConstants.EDITED_BY),
      children: contact.familyUpdatedByData?.name
    },
    {
      key: '3',
      label: t(supConstants.EDIT_DATE),
      children: dayjs(contact.familyCommentDate).format('DD/MM/YYYY HH:mm')
    },
    {
      key: '4',
      label: t(supConstants.WHY_THIS_DECISION_HAS_BEEN_MADE),
      children: contact.familyComment
    }
  ]
}

const FamilyViewStyled = styled(FamilyView)`
  margin-top: 24px;
`

const StyledWrapper = styled(ProfileContent)`
  gap: 32px;
`

const HeadingWithMargins = styled(Heading)`
  margin-left: 24px;
`

const MenuGridStyled = styled(MenuGrid)`
  margin-top: 24px;
`

const StyledSectionsWrapper = styled.div``
